/**
 * Minimum treatment-tracker model version whose meshes may be handed to a practice.
 *
 * STL export is enabled only for scans produced by the V1 model onwards. Production emits
 * V0.27.x-V0.28.x today, so every existing scan is excluded until the new model ships —
 * that is expected, not a bug.
 *
 * Changing this constant changes who can download meshes, so it is asserted in the tests:
 * a change here has to be a deliberate edit, not an accident.
 */
export const MIN_STL_MODEL_VERSION = 'V1.0.0'

// The tracker writes this into treatmentTrackerData.taskExecutionDetails.modelVersionId.
//
// Minor and patch are OPTIONAL. The producer's registry (TxTrackerVersion-production)
// holds five two-component ids — 'V0.18', 'V0.19', 'V0.20', 'V0.23', 'V0.24' — alongside
// three-component ones. Everything registered since mid-2025 uses three components and no
// two-component id has ever actually reached modelVersionId, so this is precaution rather
// than a live problem; it is worth it because requiring three components would mean a V1
// released as 'V1.0' never unlocks the button, silently and with nothing to trace.
// Missing components are treated as 0, so 'V1.0' is V1.0.0 and 'V0.18' is still rejected.
//
// Not compare-versions (already a dependency, used in TransferPatientModal): it throws on
// 'latest', '' and non-strings, so it would need this same fail-closed wrapper anyway, and
// it accepts a bare integer — see the leading-token guard below.
//
// The trailing anchor is deliberate: 'V1.0.0-rc1' is refused. A release candidate is not a
// released model, and its meshes should not reach a practice on the strength of its name.
const VERSION_PATTERN = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?$/i

const parseVersion = value => {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()

  // A bare integer is not a version. The registry's sibling `revision` attribute holds
  // exactly that shape ('37'...'55'), and reading one as a major would fail OPEN — the one
  // direction this gate must never fail. 'V1' and '1.0' still parse.
  if (!/[v.]/i.test(trimmed)) {
    return null
  }

  const match = VERSION_PATTERN.exec(trimmed)

  if (!match) {
    return null
  }

  return [Number(match[1]), Number(match[2] ?? 0), Number(match[3] ?? 0)]
}

/**
 * True when `version` is greater than or equal to `minimum`. Both are parsed leniently; an
 * unparseable operand yields false.
 *
 * Exported so the comparison can be tested against a minimum that actually discriminates.
 * Against 'V1.0.0' almost any comparison looks right — string ordering included — so tests
 * driven only through isStlModelVersionSupported cannot prove the logic.
 */
export const isVersionAtLeast = (version, minimum) => {
  const parsed = parseVersion(version)
  const parsedMinimum = parseVersion(minimum)

  if (!parsed || !parsedMinimum) {
    return false
  }

  // Compare major, then minor, then patch — never lexicographically, or 'V1.10.0' would
  // sort below 'V1.9.0' and a later model would be refused.
  for (let i = 0; i < parsed.length; i++) {
    if (parsed[i] !== parsedMinimum[i]) {
      return parsed[i] > parsedMinimum[i]
    }
  }

  return true
}

/**
 * True when `modelVersionId` is at or above {@link MIN_STL_MODEL_VERSION}.
 *
 * Fails closed: a missing or unparseable version returns false. Scans from before the field
 * existed carry no version at all, and we cannot vouch for a mesh whose origin is unknown.
 *
 * @param {string} [modelVersionId] - e.g. 'V1.0.0', from taskExecutionDetails.modelVersionId
 * @returns {boolean}
 */
export const isStlModelVersionSupported = modelVersionId => isVersionAtLeast(modelVersionId, MIN_STL_MODEL_VERSION)
